import React, { useState } from "react";
import api, { apiErr, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/lib/format";
import { StatusPill } from "@/components/StatusPill";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MapPin, FileText, Check, X, MessageSquareWarning, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const RECEIPT_PLACEHOLDER = "https://images.pexels.com/photos/14647295/pexels-photo-14647295.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function ExpenseDialog({ expense, open, onClose, onChanged }) {
  const { settings, isManager } = useAuth();
  const cur = settings.currency || "£";
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [imgErr, setImgErr] = useState(false);
  if (!expense) return null;

  const act = async (verb) => {
    if (verb === "query" && !reason.trim()) { toast.error("Please add a note for the query"); return; }
    setBusy(true);
    try {
      await api.post(`/expenses/${expense.id}/${verb}`, { reason });
      toast.success(verb === "approve" ? "Expense approved" : verb === "reject" ? "Expense rejected" : "Query raised");
      onChanged && onChanged();
      onClose();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const canReview = isManager && ["submitted", "query_raised"].includes(expense.status);
  const loc = expense.location || {};

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="expense-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between pr-6">
            <span className="font-head">{expense.merchant}</span>
            <StatusPill status={expense.status} testid="dialog-status" />
          </DialogTitle>
          <DialogDescription className="sr-only">Expense details, receipt preview and manager review actions</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-center py-3">
            <div className="font-mono tabular-nums text-4xl font-semibold text-slate-900">{money(expense.amount, cur)}</div>
            <div className="text-sm text-slate-500 mt-1">{expense.category} · {expense.ref}</div>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <Info label="Home" value={expense.site_name} />
            <Info label="Staff" value={expense.staff_name} />
            <Info label="Date" value={`${expense.purchase_date} ${expense.purchase_time || ""}`} />
            <Info label="Payment" value={expense.payment_method} />
          </div>

          {expense.description && <Info label="Items" value={expense.description} />}
          {expense.notes && <Info label="Notes" value={expense.notes} />}

          {(loc.name || loc.address || loc.lat) && (
            <div className="rounded-lg bg-slate-50 border border-border p-3">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1"><MapPin className="h-3.5 w-3.5" /> Location</div>
              <div className="text-sm text-slate-700">{loc.name}{loc.address ? ` · ${loc.address}` : ""}</div>
              {loc.lat && <div className="text-xs text-slate-400 mt-0.5">GPS: {loc.lat.toFixed(4)}, {loc.lng.toFixed(4)}</div>}
            </div>
          )}

          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Receipt</div>
            {expense.has_receipt ? (
              <a href={expense.receipt_file_id ? fileUrl(expense.receipt_file_id) : "#"} target="_blank" rel="noreferrer" data-testid="dialog-receipt" className="block relative group">
                <img src={imgErr || !expense.receipt_file_id || expense.receipt_file_id === "seed-receipt" ? RECEIPT_PLACEHOLDER : fileUrl(expense.receipt_file_id)}
                  onError={() => setImgErr(true)} alt="receipt" className="w-full max-h-[40vh] object-contain rounded-lg border border-border bg-slate-50" />
                <span className="absolute top-2 right-2 bg-white/90 rounded-md px-2 py-1 text-xs flex items-center gap-1"><ExternalLink className="h-3 w-3" /> Open</span>
              </a>
            ) : (
              <div className="flex items-center gap-2 rounded-lg border border-dashed border-red-200 bg-red-50 p-3 text-sm text-red-700"><FileText className="h-4 w-4" /> No receipt attached</div>
            )}
          </div>

          {expense.status_reason && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
              <span className="font-medium">Reviewer note:</span> {expense.status_reason}
            </div>
          )}

          {canReview && (
            <div className="pt-2 border-t border-border space-y-3">
              <Textarea data-testid="review-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Add a note (required to raise a query)" rows={2} />
              <div className="grid grid-cols-3 gap-2">
                <Button data-testid="btn-approve" disabled={busy} onClick={() => act("approve")} className="bg-emerald-600 hover:bg-emerald-700"><Check className="h-4 w-4 mr-1" /> Approve</Button>
                <Button data-testid="btn-query" disabled={busy} onClick={() => act("query")} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50"><MessageSquareWarning className="h-4 w-4 mr-1" /> Query</Button>
                <Button data-testid="btn-reject" disabled={busy} onClick={() => act("reject")} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50"><X className="h-4 w-4 mr-1" /> Reject</Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Info = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-wider font-semibold text-slate-400">{label}</div>
    <div className="text-sm text-slate-800 mt-0.5">{value || "—"}</div>
  </div>
);
